import { Controller } from "@hotwired/stimulus"
import MediumEditor from 'medium-editor'
import 'medium-editor/dist/css/medium-editor.css'
import 'medium-editor/dist/css/themes/default.css'
import { createIcons, icons } from 'lucide'

export default class extends Controller {
  static targets = [
    "editor",
    "hiddenField",
    "wordCount",
    "fontSelect",
    "fontField",
    "form",
    "saveStatus"
  ]

  static values = {
    projectTitle: String,
    fontFamily: { type: String, default: "EB Garamond" }
  }

  connect() {
    this.initEditor()
    this.updateWordCount()
    this.autosaveTimeout = null
    setTimeout(() => createIcons({ icons }), 0)
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
    }
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout)
    }
  }

  initEditor() {
    // Set initial content if hidden field has value
    if (this.hasHiddenFieldTarget && this.hiddenFieldTarget.value) {
      this.editorTarget.innerHTML = this.hiddenFieldTarget.value
    } else {
      this.editorTarget.innerHTML = this.defaultContent()
    }

    // Initialize MediumEditor with same config as legacy app
    this.editor = new MediumEditor(this.editorTarget, {
      placeholder: {
        text: 'Just start writing, highlight any text to add style...',
        hideOnClick: true
      },
      toolbar: {
        buttons: [
          {
            name: 'justifyLeft',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" x2="3" y1="6" y2="6"/><line x1="15" x2="3" y1="12" y2="12"/><line x1="17" x2="3" y1="18" y2="18"/></svg>',
          },
          {
            name: 'justifyCenter',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" x2="3" y1="6" y2="6"/><line x1="17" x2="7" y1="12" y2="12"/><line x1="19" x2="5" y1="18" y2="18"/></svg>',
          },
          {
            name: 'justifyRight',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" x2="3" y1="6" y2="6"/><line x1="21" x2="9" y1="12" y2="12"/><line x1="21" x2="7" y1="18" y2="18"/></svg>',
          },
          {
            name: 'h1',
            contentDefault: '<span style="font-size: 18px; font-weight: bold;">H1</span>',
          },
          {
            name: 'h2',
            contentDefault: '<span style="font-size: 14px; font-weight: bold;">H2</span>',
          },
          {
            name: 'h6',
            action: 'append-p',
            aria: 'paragraph',
            tagNames: ['p'],
            contentDefault: '<b>P</b>',
          },
          {
            name: 'bold',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 12a4 4 0 0 0 0-8H6v8"/><path d="M15 20a4 4 0 0 0 0-8H6v8Z"/></svg>',
          },
          {
            name: 'italic',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" x2="10" y1="4" y2="4"/><line x1="14" x2="5" y1="20" y2="20"/><line x1="15" x2="9" y1="4" y2="20"/></svg>',
          },
          {
            name: 'underline',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4v6a6 6 0 0 0 12 0V4"/><line x1="4" x2="20" y1="20" y2="20"/></svg>',
          },
          {
            name: 'quote',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3z"/></svg>',
          },
        ]
      },
      keyboardCommands: {
        commands: [
          { command: 'bold', key: 'B', meta: true, shift: false, alt: false },
          { command: 'italic', key: 'I', meta: true, shift: false, alt: false },
          { command: 'underline', key: 'U', meta: true, shift: false, alt: false },
          { command: 'append-blockquote', key: 'B', meta: true, shift: true, alt: false },
          { command: 'append-h2', key: 'H', meta: true, shift: true, alt: false },
        ],
      }
    })

    // Subscribe to content changes
    this.editor.subscribe('editableInput', () => {
      this.syncContent()
      this.updateWordCount()
      this.scheduleAutosave()
    })

    // Apply initial font
    this.applyFont(this.fontFamilyValue)
  }

  defaultContent() {
    return `
      <h1 style="text-align: center;">${this.projectTitleValue || 'Untitled'}</h1>
      <p style="text-align: center;"><em>By Your Name</em></p>
      <p><br></p>
      <p><br></p>
      <h2 style="text-align: center;"><u>Chapter 1</u></h2>
      <p><br></p>
      <p>Start writing here...</p>
    `
  }

  syncContent() {
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = this.editorTarget.innerHTML
    }
  }

  updateWordCount() {
    if (!this.hasWordCountTarget || !this.hasEditorTarget) return

    const text = this.editorTarget.innerText || ''
    const matches = text.trim().match(/\S+/g)
    const words = matches ? matches.length : 0
    this.wordCountTarget.textContent = words.toLocaleString()
  }

  // Font family
  changeFont(event) {
    const font = event.target.value
    this.fontFamilyValue = font
    this.applyFont(font)

    // Update hidden field
    if (this.hasFontFieldTarget) {
      this.fontFieldTarget.value = font
    }
  }

  applyFont(font) {
    if (this.hasEditorTarget) {
      // Remove all font classes first
      this.editorTarget.classList.remove('font-garamond', 'font-georgia', 'font-baskerville')

      // Add the appropriate font class
      const fontClassMap = {
        'EB Garamond': 'font-garamond',
        'Noto Serif': 'font-georgia',
        'Libre Baskerville': 'font-baskerville'
      }
      const fontClass = fontClassMap[font]
      if (fontClass) {
        this.editorTarget.classList.add(fontClass)
      }
    }
  }

  refreshWordCount() {
    this.updateWordCount()
  }

  // Autosave functionality
  scheduleAutosave() {
    if (this.autosaveTimeout) {
      clearTimeout(this.autosaveTimeout)
    }
    this.autosaveTimeout = setTimeout(() => this.autosave(), 2000)
  }

  async autosave() {
    if (!this.hasHiddenFieldTarget || !this.hasFormTarget) return

    this.showSaveStatus("Saving...")

    try {
      const response = await fetch(this.formTarget.action, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        body: JSON.stringify({
          script: {
            body: this.hiddenFieldTarget.value,
            font_family: this.hasFontFieldTarget ? this.fontFieldTarget.value : null
          }
        })
      })

      if (response.ok) {
        this.showSaveStatus("Saved")
        setTimeout(() => this.hideSaveStatus(), 2000)
      } else {
        this.showSaveStatus("Save failed", true)
      }
    } catch (error) {
      console.error("Autosave error:", error)
      this.showSaveStatus("Save failed", true)
    }
  }

  showSaveStatus(text, isError = false) {
    if (this.hasSaveStatusTarget) {
      this.saveStatusTarget.textContent = text
      this.saveStatusTarget.classList.remove("hidden", "text-red-600", "text-gray-500")
      this.saveStatusTarget.classList.add(isError ? "text-red-600" : "text-gray-500")
    }
  }

  hideSaveStatus() {
    if (this.hasSaveStatusTarget) {
      this.saveStatusTarget.classList.add("hidden")
    }
  }

  // PDF download for novels using html2pdf
  async downloadPdf() {
    const content = this.editorTarget.innerHTML
    const font = this.fontFamilyValue
    const title = this.projectTitleValue || 'Novel'

    // Dynamically import html2pdf
    const html2pdfModule = await import('html2pdf.js/dist/html2pdf.bundle.min.js')
    const html2pdf = html2pdfModule.default

    // Create a temporary container for PDF generation
    const container = document.createElement('div')
    container.innerHTML = content
    container.style.cssText = `
      font-family: "${font}", Georgia, serif;
      font-size: 12pt;
      line-height: 1.8;
      color: #000;
    `

    // Apply font to all elements while preserving their existing styles
    container.querySelectorAll('*').forEach(el => {
      el.style.fontFamily = `"${font}", Georgia, serif`
    })

    // Style headings - preserve text-align if set
    container.querySelectorAll('h1').forEach(el => {
      const align = el.style.textAlign || 'left'
      el.style.fontSize = '24pt'
      el.style.marginBottom = '0.5em'
      el.style.fontWeight = 'bold'
      el.style.textAlign = align
    })
    container.querySelectorAll('h2').forEach(el => {
      const align = el.style.textAlign || 'left'
      el.style.fontSize = '18pt'
      el.style.marginTop = '1.5em'
      el.style.marginBottom = '0.5em'
      el.style.fontWeight = 'bold'
      el.style.textAlign = align
    })
    container.querySelectorAll('p').forEach(el => {
      const align = el.style.textAlign || 'left'
      el.style.marginBottom = '0.5em'
      el.style.textAlign = align
    })
    container.querySelectorAll('blockquote').forEach(el => {
      el.style.margin = '1em 2em'
      el.style.fontStyle = 'italic'
      el.style.borderLeft = '3px solid #ccc'
      el.style.paddingLeft = '1em'
    })

    const opt = {
      margin: [0.75, 1, 0.75, 1], // top, right, bottom, left in inches
      filename: `${title.replace(/[^a-z0-9]/gi, '_')}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
    }

    html2pdf().set(opt).from(container).save()
  }
}
